<template>
    <div class="fmd-editor">
        <div class="table-responsive">
            <table class="table table-sm table-bordered fmd-table">
                <thead class="thead-light">
                    <tr>
                        <th class="col-component">{{ $t('Component(FMDEditor):component') }}</th>
                        <th class="col-material">{{ $t('Component(FMDEditor):material') }}</th>
                        <th class="col-weight">{{ $t('Component(FMDEditor):material-weight') }}</th>
                        <th class="col-substance">{{ $t('Component(FMDEditor):substance') }}</th>
                        <th class="col-weight">{{ $t('Component(FMDEditor):substance-weight') }}</th>
                        <th v-if="showComment" class="col-comment">{{ $t('Component(FMDEditor):comment') }}</th>
                        <th class="col-lists">{{ $t('Component(Label):substance-list') }}</th>
                        <th class="col-actions" v-if="!readonly"></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="componentGroup in componentGroups">
                        <template v-for="(group, groupIndex) in componentGroup.declarations">
                            <FmdComponentRow
                                :key="group.localKey"
                                :group="group"
                                :component-group="componentGroup"
                                :is-primary-row="groupIndex === 0"
                                :readonly="readonly"
                                :touched="touchedRows[group.localKey]"
                                :material-label="getMaterialLabel(group.materialId)"
                                :material-category-options="materialCategoryOptions"
                                :primary-substance="group.substances[0]"
                                :primary-substance-name="getPrimarySubstanceName(group)"
                                :primary-substance-meta="getSubstanceMeta(group, 0)"
                                :primary-substance-option="getSubstanceOption(group.substances[0])"
                                :material-weight-display="formatWeightRangeWithOperators(group.materialWeight.from, group.materialWeight.to, group.materialWeight.fromOperator, group.materialWeight.toOperator, group.materialWeight.unit)"
                                :primary-substance-weight-display="formatWeightRangeWithOperators(group.substances[0].weight.from, group.substances[0].weight.to, group.substances[0].weight.fromOperator, group.substances[0].weight.toOperator, group.substances[0].weight.unit)"
                                :material-error="getGroupError(group.localKey, 'material')"
                                :component-error="getGroupError(group.localKey, 'component')"
                                :primary-substance-error="getSubstanceError(group.localKey, 0, 'substanceId')"
                                :material-weight-error="getMaterialWeightError(group.localKey)"
                                :primary-substance-weight-error="getSubstanceWeightError(group.localKey, 0)"
                                :material-warning="getMaterialWarning(group.localKey)"
                                :show-comment="showComment"
                                :primary-substance-lists-text="getSubstanceListsText(group.substances[0].substanceId)"
                                :primary-substance-lists-extra="getSubstanceListsExtra(group.substances[0].substanceId)"
                                :allow-no-substance="allowNoSubstance(group)"
                                :no-substance-label="noSubstanceLabel"
                                :search-fn="resolvedApi.searchSubstances"
                                :substance-placeholder="$t('Component(FMDEditor):substance-placeholder')"
                                :start-typing-text="$t('Component(FMDEditor):substance-start-typing')"
                                :no-options-text="$t('Component(FMDEditor):substance-no-options')"
                                :request-add-text="$t('Component(FMDEditor):request-add-substance')"
                                :delete-text="$t('Component(FMDEditor):delete-group')"
                                :is-substance-selected="isSubstanceSelected"
                                @component-change="onComponentGroupChange"
                                @material-change="onMaterialDeclarationChange"
                                @primary-substance-select="onPrimarySubstanceSelected"
                                @request-add-substance="requestAddSubstance"
                                @material-weight-change="onMaterialWeightChange"
                                @substance-weight-change="onSubstanceWeightChange"
                                @weight-editing="onWeightEditing"
                                @remove="onRemovePrimarySubstance"
                            />

                            <FmdSubstanceRow
                                v-for="(substance, substanceIndex) in group.substances.slice(1)"
                                :key="substance.rowId"
                                :group="group"
                                :substance="substance"
                                :substance-index="substanceIndex + 1"
                                :readonly="readonly"
                                    :material-label="group.component"
                                    :component-name="getMaterialLabel(group.materialId)"
                                :substance-meta="getSubstanceMeta(group, substanceIndex + 1)"
                                :substance-option="getSubstanceOption(substance)"
                                :material-weight-display="formatWeightRangeWithOperators(group.materialWeight.from, group.materialWeight.to, group.materialWeight.fromOperator, group.materialWeight.toOperator, group.materialWeight.unit)"
                                :substance-weight-display="formatWeightRangeWithOperators(substance.weight.from, substance.weight.to, substance.weight.fromOperator, substance.weight.toOperator, substance.weight.unit)"
                                :substance-error="getSubstanceError(group.localKey, substanceIndex + 1, 'substanceId')"
                                :substance-weight-error="getSubstanceWeightError(group.localKey, substanceIndex + 1)"
                                :show-comment="showComment"
                                layout="component-first"
                                :allow-no-substance="allowNoSubstance(group)"
                                :no-substance-label="noSubstanceLabel"
                                :substance-lists-text="getSubstanceListsText(substance.substanceId)"
                                :substance-lists-extra="getSubstanceListsExtra(substance.substanceId)"
                                :touched="touchedRows[group.localKey]"
                                :search-fn="resolvedApi.searchSubstances"
                                :substance-placeholder="$t('Component(FMDEditor):substance-placeholder')"
                                :start-typing-text="$t('Component(FMDEditor):substance-start-typing')"
                                :no-options-text="$t('Component(FMDEditor):substance-no-options')"
                                :request-add-text="$t('Component(FMDEditor):request-add-substance')"
                                :delete-text="$t('Component(FMDEditor):delete-substance')"
                                :is-substance-selected="isSubstanceSelected"
                                @substance-select="onSubstanceSelected"
                                @request-add-substance="requestAddSubstance"
                                @substance-weight-change="onSubstanceWeightChange"
                                @substance-comment-change="onSubstanceCommentChange"
                                @weight-editing="onWeightEditing"
                                @remove-substance="removeSubstance"
                            />

                            <FmdActionsRow
                                :readonly="readonly"
                                :show-add-component="groupIndex === (componentGroup.declarations.length - 1) && !!(componentGroup.component && componentGroup.component.trim())"
                                :show-add-substance="shouldShowAddSubstance(group)"
                                :show-comment="showComment"
                                layout="component-first"
                                :add-component-text="$t('Component(FMDEditor):add-material')"
                                :add-substance-text="$t('Component(FMDEditor):add-substance')"
                                @add-component="addMaterial(componentGroup)"
                                @add-substance="addSubstance(group)"
                            />
                        </template>
                    </template>

                    <tr v-if="!readonly" class="action-row">
                        <td :colspan="showComment ? 7 : 6">
                            <b-button size="sm" variant="outline-primary" @click="addComponentGroup">
                                {{ $t('Component(FMDEditor):add-component') }}
                            </b-button>
                        </td>
                        <td></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <AddSubstanceModal
            ref="addSubstanceModal"
            @created="onSubstanceCreated"
        />
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import { BButton } from 'bootstrap-vue'
import { SubstanceApi } from '@core/api'
import weightRangeMixin from '@/views/product/components/product-card/fmd/mixins/weightRange'
import validationHelpersMixin from '@/views/product/components/product-card/fmd/mixins/validationHelpers'
import substanceHelpersMixin from '@/views/product/components/product-card/fmd/mixins/substanceHelpers'
import noSubstanceHelpersMixin from '@/views/product/components/product-card/fmd/mixins/noSubstanceHelpers'
import weightHelpersMixin from '@/views/product/components/product-card/fmd/mixins/weightHelpers'
import noSubstanceRulesMixin from '@/views/product/components/product-card/fmd/mixins/noSubstanceRules'
import weightEditingHelpersMixin from '@/views/product/components/product-card/fmd/mixins/weightEditingHelpers'
import FmdComponentRow from '@/views/product/components/product-card/fmd/common/FmdComponentRow.vue'
import FmdSubstanceRow from '@/views/product/components/product-card/fmd/common/FmdSubstanceRow.vue'
import FmdActionsRow from '@/views/product/components/product-card/fmd/common/FmdActionsRow.vue'
import AddSubstanceModal from '@/views/product/components/product-card/AddSubstanceModal.vue'

export default {
    name: 'ComponentFirstFMDEditor',
    mixins: [
        weightRangeMixin,
        validationHelpersMixin,
        substanceHelpersMixin,
        noSubstanceHelpersMixin,
        noSubstanceRulesMixin,
        weightHelpersMixin,
        weightEditingHelpersMixin,
    ],
    components: {
        BButton,
        FmdComponentRow,
        FmdSubstanceRow,
        FmdActionsRow,
        AddSubstanceModal,
    },
    props: {
        productId: {
            type: [Number, String],
            required: true,
        },
        initialVersion: {
            type: Number,
            required: true,
        },
        initialDeclarations: {
            type: Array,
            default() {
                return []
            },
        },
        materialsCatalog: {
            type: Array,
            default() {
                return []
            },
        },
        api: {
            type: Object,
            default: null,
        },
        ensureSubstanceListsFn: {
            type: Function,
            default: null,
        },
        getSubstanceListsTextFn: {
            type: Function,
            default: null,
        },
        getSubstanceListsExtraFn: {
            type: Function,
            default: null,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        showComment: {
            type: Boolean,
            default: false,
        },
        requireComponent: {
            type: Boolean,
            default: true,
        },
        requireSubstance: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            declarations: [],
            dirty: false,
            materialCategories: [],
            touchedRows: {},
            pendingAddSubstance: null,
        }
    },
    computed: {
        resolvedApi() {
            return {
                searchSubstances: this.api && this.api.searchSubstances ? this.api.searchSubstances : SubstanceApi.search,
            }
        },
        noSubstanceLabel() {
            return this.$t('Component(FMDEditor):no-substance')
        },
        materialCategoryOptions() {
            if (this.materialCategories.length) {
                return this.materialCategories
            }
            return this.materialsCatalog.map(item => ({ id: item.id, title: item.name || item.title }))
        },
        componentGroups() {
            const groups = []
            const index = {}
            this.declarations.forEach(declaration => {
                const normalizedComponent = (declaration.component || '').trim().toLowerCase()
                const key = normalizedComponent ? `cmp-${normalizedComponent}` : `tmp-${declaration.localKey}`
                if (!index[key]) {
                    index[key] = { key, component: declaration.component || '', declarations: [] }
                    groups.push(index[key])
                }
                index[key].component = declaration.component || ''
                index[key].declarations.push(declaration)
            })
            return groups
        },
        validation() {
            return this.validateDeclarations(this.declarations)
        },
    },
    created() {
        this.reset()
        this.loadMaterialCategories()
    },
    watch: {
        initialVersion() {
            this.reset()
        },
        initialDeclarations: {
            deep: true,
            handler(next) {
                if (this.isSameDeclarations(next)) {
                    return
                }
                this.reset()
            },
        },
    },
    methods: {
        isSameDeclarations(next) {
            try {
                const current = this.buildPayloadDeclarations()
                return JSON.stringify(next || []) === JSON.stringify(current || [])
            } catch (error) {
                return false
            }
        },
        loadMaterialCategories() {
            SubstanceApi.materialCategories().then(response => {
                this.materialCategories = response.data || []
            })
        },
        reset() {
            this.declarations = this.normalizeDeclarations(this.initialDeclarations)
            this.dirty = false
            this.touchedRows = {}
            this.pendingAddSubstance = null
            this.resetWeightEditing()
            this.prefetchSubstanceLists()
        },
        normalizeDeclarations(declarations) {
            return (declarations || []).map(item => {
                const substances = this.normalizeSubstances(item.substances)
                const materialWeight = item.materialWeight || item.weight || {}
                return {
                declarationId: item.declarationId || item.id || null,
                localKey: (item.declarationId || item.id) ? `d-${item.declarationId || item.id}` : `tmp-${uuidv4()}`,
                component: item.component || '',
                materialId: item.materialId || (item.material && item.material.id) || null,
                materialCategory: item.materialCategory
                    || (item.material ? { id: item.material.id, title: item.material.title || item.material.name } : null)
                    || this.getMaterialNode(item.materialId || (item.material && item.material.id)),
                materialWeight: this.normalizeWeight(materialWeight),
                substances,
                expanded: true,
            }
            })
        },
        addComponentGroup() {
            this.declarations.push({
                declarationId: null,
                localKey: `tmp-${uuidv4()}`,
                component: '',
                materialId: null,
                materialCategory: null,
                materialWeight: this.normalizeWeight(),
                substances: [this.createEmptySubstance()],
                expanded: true,
            })
            this.$set(this.touchedRows, this.declarations[this.declarations.length - 1].localKey, true)
            this.markDirty()
        },
        removeDeclaration(localKey) {
            const index = this.declarations.findIndex(item => item.localKey === localKey)
            if (index >= 0) {
                this.declarations.splice(index, 1)
            }
            this.$delete(this.touchedRows, localKey)
            this.markDirty()
        },
        addMaterial(componentGroup) {
            const base = componentGroup && componentGroup.declarations[0]
            this.declarations.push({
                declarationId: null,
                localKey: `tmp-${uuidv4()}`,
                component: base ? base.component : '',
                materialId: null,
                materialCategory: null,
                materialWeight: this.normalizeWeight(),
                substances: [this.createEmptySubstance()],
                expanded: true,
            })
            this.$set(this.touchedRows, this.declarations[this.declarations.length - 1].localKey, true)
            this.markDirty()
        },
        addSubstance(group) {
            if (this.groupHasNoSubstance(group)) {
                return
            }
            group.substances.push(this.createEmptySubstance())
            this.markDirty()
        },
        removeSubstance(group, index) {
            const removedSubstance = this.getRemovedSubstancePayload(group.substances[index])
            group.substances.splice(index, 1)
            if (group.substances.length === 0) {
                group.substances.push(this.createEmptySubstance({ noSubstance: true }))
            }
            this.markDirty()
            this.emitSubstanceRemoved(removedSubstance)
        },
        onRemovePrimarySubstance(localKey) {
            const group = this.declarations.find(item => item.localKey === localKey)
            const removedSubstance = group && group.substances ? this.getRemovedSubstancePayload(group.substances[0]) : null
            this.removeDeclaration(localKey)
            this.emitSubstanceRemoved(removedSubstance)
        },
        onComponentGroupChange(componentGroup, value) {
            const targetGroup = componentGroup && componentGroup.declarations ? componentGroup.declarations : []
            targetGroup.forEach(group => {
                group.component = value
                this.$set(this.touchedRows, group.localKey, true)
            })
            this.markDirty()
        },
        onMaterialDeclarationChange(group, value) {
            const picked = value || null
            group.materialCategory = picked
            group.materialId = picked ? picked.id : null
            this.$set(this.touchedRows, group.localKey, true)
            this.markDirty()
        },
        onMaterialWeightChange(group, value) {
            const next = this.applyWeightChange(group.materialWeight, value)
            group.materialWeight = next.weight
            this.$set(this.touchedRows, group.localKey, true)
            this.markDirty()
        },
        onSubstanceWeightChange(group, substance, value) {
            const next = this.applyWeightChange(substance.weight, value)
            substance.weight = next.weight
            substance.weightText = next.weightText
            this.$set(this.touchedRows, group.localKey, true)
            this.markDirty()
        },
        onSubstanceCommentChange(group, substance, value) {
            substance.comment = value
            this.$set(this.touchedRows, group.localKey, true)
            this.markDirty()
        },
        onSubstanceSelected(group, substanceIndex, event) {
            this.ensureSubstance(group, substanceIndex)
            const substance = group.substances[substanceIndex]
            const removedSubstance = this.getRemovedSubstancePayload(substance)

            if (!event) {
                this.clearSubstanceSelection(substance)
                this.$set(this.touchedRows, group.localKey, true)
                this.markDirty()
                this.emitSubstanceRemoved(removedSubstance)
                return
            }

            const substanceId = this.applySubstanceSelection(substance, event)
            if (substanceId) {
                this.ensureSubstanceLists(substanceId)
            }
            this.$set(this.touchedRows, group.localKey, true)
            this.markDirty()
            if (removedSubstance && removedSubstance.id !== substanceId) {
                this.emitSubstanceRemoved(removedSubstance)
            }
        },
        onPrimarySubstanceSelected(group, event) {
            this.onSubstanceSelected(group, 0, event)
        },
        ensureSubstance(group, index) {
            while (group.substances.length <= index) {
                group.substances.push(this.createEmptySubstance())
            }
        },
        getPrimarySubstanceName(group) {
            if (group.substances && group.substances[0] && group.substances[0].substanceName) {
                return group.substances[0].substanceName
            }
            return '-'
        },
        requestAddSubstance(group, substanceIndex, query) {
            this.pendingAddSubstance = {
                localKey: group.localKey,
                substanceIndex,
                query,
            }
            this.$refs.addSubstanceModal.show()
        },
        onSubstanceCreated(substance) {
            if (!this.pendingAddSubstance) {
                return
            }
            const group = this.declarations.find(item => item.localKey === this.pendingAddSubstance.localKey)
            if (!group) {
                this.pendingAddSubstance = null
                return
            }
            this.onSubstanceSelected(group, this.pendingAddSubstance.substanceIndex, {
                id: substance.id,
                name: substance.name || substance.title || '',
                title: substance.title || substance.name || '',
                cas: substance.cas || '',
                ec: substance.ec || '',
            })
            this.pendingAddSubstance = null
        },
        prefetchSubstanceLists() {
            const ids = new Set()
            this.declarations.forEach(group => {
                (group.substances || []).forEach(substance => {
                    if (substance && substance.substanceId) {
                        ids.add(substance.substanceId)
                    }
                })
            })
            ids.forEach(id => this.ensureSubstanceLists(id))
        },
        ensureSubstanceLists(substanceId) {
            if (!substanceId || !this.ensureSubstanceListsFn) {
                return Promise.resolve(null)
            }
            return this.ensureSubstanceListsFn(substanceId)
        },
        getSubstanceListsText(substanceId) {
            if (!this.getSubstanceListsTextFn) {
                return '-'
            }
            return this.getSubstanceListsTextFn(substanceId)
        },
        getSubstanceListsExtra(substanceId) {
            if (!this.getSubstanceListsExtraFn) {
                return 0
            }
            return this.getSubstanceListsExtraFn(substanceId)
        },
        markDirty() {
            if (!this.dirty) {
                this.dirty = true
            }
            this.emitChange()
        },
        getRemovedSubstancePayload(substance) {
            if (!substance || !substance.substanceId) {
                return null
            }
            return {
                id: substance.substanceId,
                title: substance.substanceName || '',
                cas: substance.substanceCas || '',
                ec: substance.substanceEc || '',
            }
        },
        clearSubstanceSelection(substance) {
            substance.noSubstance = false
            substance.substanceId = null
            substance.substanceName = ''
            substance.substanceCas = ''
            substance.substanceEc = ''
            substance.weight = this.normalizeWeight()
            substance.weightText = ''
        },
        emitSubstanceRemoved(substance) {
            if (!substance) {
                return
            }
            this.$emit('substance-removed', substance)
        },
        emitChange() {
            const validation = this.validation
            const hasUnfilledRows = this.hasUnfilledRows(this.declarations)
            const isWeightEditing = this.isWeightEditing()
            if (validation.hasErrors) {
                this.$emit('invalid', {
                    dirty: this.dirty,
                    errors: validation.errors,
                    warnings: validation.warnings,
                    isWeightEditing,
                    hasUnfilledRows,
                    declarations: this.buildPayloadDeclarations(),
                })
                return
            }
            this.$emit('change', {
                dirty: this.dirty,
                isWeightEditing,
                hasUnfilledRows,
                declarations: this.buildPayloadDeclarations(),
            })
        },
        buildPayloadDeclarations() {
            return this.declarations.map(group => ({
                id: group.declarationId,
                component: group.component,
                material: group.materialId ? {
                    id: group.materialId,
                    title: this.getMaterialLabel(group.materialId),
                } : null,
                weight: this.buildWeightPayload(group.materialWeight),
                substances: group.substances
                    .filter(sub => sub.substanceId)
                    .map(sub => ({
                    substance: sub.substanceId ? {
                        id: sub.substanceId,
                        title: sub.substanceName || '',
                        cas: sub.substanceCas || '',
                        ec: sub.substanceEc || '',
                    } : null,
                    weight: this.buildWeightPayload(sub.weight),
                    comment: null,
                })),
            }))
        },
        validateDeclarations(declarations) {
            const errors = { groups: {} }
            const warnings = { groups: {} }
            let hasErrors = false
            const groupKeyMap = {}
            let totalMaterialFrom = 0
            const allGroupIds = []

            const addError = (groupId, field, message) => {
                if (!errors.groups[groupId]) {
                    errors.groups[groupId] = {
                        component: [],
                        material: [],
                        materialWeightFrom: [],
                        materialWeightTo: [],
                        substances: [],
                    }
                }
                errors.groups[groupId][field].push(message)
                hasErrors = true
            }

            const ensureSubstanceErrors = (groupId, index) => {
                if (!errors.groups[groupId]) {
                    errors.groups[groupId] = {
                        component: [],
                        material: [],
                        materialWeightFrom: [],
                        materialWeightTo: [],
                        substances: [],
                    }
                }
                if (!errors.groups[groupId].substances[index]) {
                    errors.groups[groupId].substances[index] = {
                        substanceId: [],
                        weightFrom: [],
                        weightTo: [],
                    }
                }
            }

            const addSubstanceError = (groupId, index, field, message) => {
                ensureSubstanceErrors(groupId, index)
                errors.groups[groupId].substances[index][field].push(message)
                hasErrors = true
            }

            const normalizeOperator = (value, fallback) => {
                const normalized = String(value || fallback || '').toUpperCase()
                if (['GT', 'GTE', 'LT', 'LTE'].includes(normalized)) {
                    return normalized
                }
                return fallback
            }

            const validateWeight = (range, groupId, prefix, index) => {
                const from = range.from
                const to = range.to
                const fromOperator = normalizeOperator(range.fromOperator, 'GTE')
                const toOperator = normalizeOperator(range.toOperator, 'LTE')
                const hasFrom = !(from === null || from === undefined || from === '')
                const hasTo = !(to === null || to === undefined || to === '')
                if (!hasFrom && !hasTo) {
                    if (index === null) {
                        addError(groupId, `${prefix}From`, this.$t('Component(FMDEditor):error-weight-required'))
                        addError(groupId, `${prefix}To`, this.$t('Component(FMDEditor):error-weight-required'))
                    } else {
                        addSubstanceError(groupId, index, 'weightFrom', this.$t('Component(FMDEditor):error-weight-required'))
                        addSubstanceError(groupId, index, 'weightTo', this.$t('Component(FMDEditor):error-weight-required'))
                    }
                }
                if (hasFrom && (from < 0 || from > 100 || (from === 0 && fromOperator !== 'GT'))) {
                    if (index === null) {
                        addError(groupId, `${prefix}From`, this.$t('Component(FMDEditor):error-weight-percent-range'))
                    } else {
                        addSubstanceError(groupId, index, 'weightFrom', this.$t('Component(FMDEditor):error-weight-percent-range'))
                    }
                }
                if (hasTo && (to <= 0 || to > 100)) {
                    if (index === null) {
                        addError(groupId, `${prefix}To`, this.$t('Component(FMDEditor):error-weight-percent-range'))
                    } else {
                        addSubstanceError(groupId, index, 'weightTo', this.$t('Component(FMDEditor):error-weight-percent-range'))
                    }
                }
                if (hasFrom && hasTo && (from > to || (from === to && (fromOperator !== 'GTE' || toOperator !== 'LTE')))) {
                    if (index === null) {
                        addError(groupId, `${prefix}From`, this.$t('Component(FMDEditor):error-weight-order'))
                        addError(groupId, `${prefix}To`, this.$t('Component(FMDEditor):error-weight-order'))
                    } else {
                        addSubstanceError(groupId, index, 'weightFrom', this.$t('Component(FMDEditor):error-weight-order'))
                        addSubstanceError(groupId, index, 'weightTo', this.$t('Component(FMDEditor):error-weight-order'))
                    }
                }
            }

            declarations.forEach((group, groupIndex) => {
                const groupId = group.localKey || `group-${groupIndex}`
                allGroupIds.push(groupId)
                if (this.requireComponent && (!group.component || !group.component.trim())) {
                    addError(groupId, 'component', this.$t('Component(FMDEditor):error-component-required'))
                }
                if (!group.materialId) {
                    addError(groupId, 'material', this.$t('Component(FMDEditor):error-material-required'))
                }

                validateWeight(group.materialWeight, groupId, 'materialWeight', null)
                if (group.materialWeight && group.materialWeight.from !== null && group.materialWeight.from !== undefined && group.materialWeight.from !== '') {
                    const fromValue = Number(group.materialWeight.from)
                    if (!Number.isNaN(fromValue)) {
                        totalMaterialFrom += fromValue
                    }
                }

                if (group.materialId) {
                    const normalizedComponent = (group.component || '').trim().toLowerCase()
                    const key = normalizedComponent
                        ? `cmp:${normalizedComponent}:mat:${group.materialId}`
                        : `mat:${group.materialId}`
                    if (groupKeyMap[key]) {
                        addError(groupId, 'material', this.$t('Component(FMDEditor):error-unique-group'))
                        addError(groupKeyMap[key], 'material', this.$t('Component(FMDEditor):error-unique-group'))
                        if (normalizedComponent) {
                            addError(groupId, 'component', this.$t('Component(FMDEditor):error-unique-group'))
                            addError(groupKeyMap[key], 'component', this.$t('Component(FMDEditor):error-unique-group'))
                        }
                    } else {
                        groupKeyMap[key] = groupId
                    }
                }

                const substanceIdMap = {}
                let sumSubstanceMax = 0
                group.substances.forEach((substance, subIndex) => {
                    const hasSubstance = Boolean(substance.substanceId)
                    const hasAnyWeight = (substance.weight && ((substance.weight.from !== null && substance.weight.from !== undefined && substance.weight.from !== '')
                        || (substance.weight.to !== null && substance.weight.to !== undefined && substance.weight.to !== '')))
                    if (!hasSubstance && (this.requireSubstance || hasAnyWeight)) {
                        addSubstanceError(groupId, subIndex, 'substanceId', this.$t('Component(FMDEditor):error-substance-required'))
                    }
                    if (hasSubstance) {
                        if (substanceIdMap[substance.substanceId] !== undefined) {
                            addSubstanceError(groupId, subIndex, 'substanceId', this.$t('Component(FMDEditor):error-substance-unique'))
                            addSubstanceError(groupId, substanceIdMap[substance.substanceId], 'substanceId', this.$t('Component(FMDEditor):error-substance-unique'))
                        } else {
                            substanceIdMap[substance.substanceId] = subIndex
                        }
                    }
                    if (hasSubstance || hasAnyWeight || this.requireSubstance) {
                        validateWeight(substance.weight, groupId, null, subIndex)
                    }
                    if (substance.weight.to !== null) {
                        sumSubstanceMax += substance.weight.to
                    }
                })
                if (sumSubstanceMax > 100) {
                    warnings.groups[groupId] = true
                }
            })
            if (totalMaterialFrom > 100) {
                allGroupIds.forEach(groupId => {
                    addError(groupId, 'materialWeightFrom', this.$t('Component(FMDEditor):error-material-weight-sum-exceeds-100'))
                })
            }

            return { errors, warnings, hasErrors }
        },
        getMaterialLabel(id) {
            const found = this.findMaterialNode(id)
            return found ? (found.name || found.title || '') : ''
        },
        getMaterialNode(id) {
            if (!id) {
                return null
            }
            const found = this.findMaterialNode(id)
            return found ? { id: found.id, title: found.name || found.title } : null
        },
        findMaterialNode(id) {
            if (!id) {
                return null
            }
            const source = this.materialCategories.length ? this.materialCategories : this.materialsCatalog
            const stack = Array.isArray(source) ? [...source] : []
            while (stack.length > 0) {
                const item = stack.shift()
                if (item.id === id) {
                    return item
                }
                if (item.children && item.children.length) {
                    stack.push(...item.children)
                }
            }
            return null
        },
    },
}
</script>

<style scoped>
.fmd-table th,
.fmd-table td {
    vertical-align: top;
}
.col-actions {
    width: 60px;
}
.col-material {
    width: 180px;
}
.col-component {
    width: 160px;
}
.col-substance {
    width: 300px;
}
.col-weight {
    max-width: 220px;
    min-width: 150px;
    width: 1%;
    white-space: nowrap;
}
.col-comment {
    width: 180px;
}
.col-lists {
    width: 180px;
}
:deep(.group-row) {
    background-color: #f9fafb;
}
:deep(.substance-row td) {
    background-color: #fff;
}
:deep(.action-row td) {
    background-color: #fbfbfd;
}
:deep(.text-muted-dark) {
    color: #6c757d;
}
:deep(.weight-input) {
    min-width: 110px;
    max-width: 130px;
}
:deep(.vue-treeselect__menu-container),
:deep(.vue-treeselect__menu) {
    min-width: 320px;
    max-width: 480px;
}
:deep(.vue-treeselect__option),
:deep(.vue-treeselect__label) {
    white-space: normal;
    word-break: break-word;
}
</style>
